<template>
  <v-dialog
      max-width="600px"
      v-model="dialog"
      :width="dialogWidth"
      @keydown.enter.prevent="save"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Christmas Offer</span>
      </v-card-title>
      <v-card-text class="resizable-card__body">
        <v-row dense>
          <v-col>
            <v-text-field
                label="Name"
                v-model.trim="model.name"
                v-ascii-only
            />
          </v-col>

          <v-col>
            <v-text-field
                label="Days"
                v-model="daysText"
                :rules="[daysRule]"
                hint="Example: 1,2,13,24"
                persistent-hint
                clearable
                @blur="prettyFormat"
                @keydown.enter.prevent="prettyFormat(); onenter()"
                v-ascii-only
            />
          </v-col>
        </v-row>

        <v-row dense>
          <r-christmas-tiers :model.sync="model.tierLevels" />
        </v-row>

        <v-row dense class="mt-4">
          <r-christmas-products :model.sync="model.products" />
        </v-row>

      </v-card-text>
      <v-card-actions class="resizable-card__actions">
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn @click="save" color="blue darken-1" text>
          Save
        </v-btn>
      </v-card-actions>
      <div class="resize-handle" @mousedown="startResize"></div>
    </v-card>
  </v-dialog>
</template>

<script>
import {ViewRules} from "@/common/ViewRules";
import {SmartOfferChristmasConfig} from "@/modules/redactor/models/smart-offer/bundle-christmas/SmartOfferChristmasConfig";

import RChristmasProducts from "@/modules/redactor/views/smart-offer/bundle-christmas/r-christmas-products.vue";
import RChristmasTiers from "@/modules/redactor/views/smart-offer/bundle-christmas/r-christmas-tiers.vue";

export default {
  name: "r-bundle-christmas-dialog",
  components: {RChristmasTiers, RChristmasProducts},
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: new SmartOfferChristmasConfig(),
    rules: ViewRules.READONLY,
    daysText: "",
    _typingNormalizeLock: false,
    dialogWidth: 690,
    dialogHeight: 494,
    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 690,
    resizeStartHeight: 494,
    viewportPadding: 32
  }),

  computed: {
    daysRule() {
      return (val) => {
        const duplicate = this.findDuplicateToken(val);
        if (duplicate != null) return `Duplicate values are not allowed: ${duplicate}`;

        const amount = this.findFirstTooBigToken(val);
        return amount ? `Value out of range: ${amount} (allowed 1–31)` : true;
      };
    },
  },

  watch: {
    daysText(val) {
      if (this._typingNormalizeLock) return;

      const normalized = this.normalizeSeparators(val);
      if (normalized !== val) {
        this._typingNormalizeLock = true;
        this.$nextTick(() => {
          this.daysText = normalized;
          this._typingNormalizeLock = false;
        });
        this.model.days = this.parseDays(normalized);
        return;
      }
      this.model.days = this.parseDays(val);
    }
  },

  methods: {
    normalizeSeparators(s) {
      return String(s)
          .replace(/[.\s]+/g, ',')
          .replace(/,{2,}/g, ',');
    },

    parseDays(input) {
      const numbers = String(input)
          .replace(/[;|\n]/g, ',')
          .split(/[,\s]+/)
          .map(s => parseInt(s, 10))
          .filter(n => Number.isFinite(n) && n >= 1 && n <= 31);

      return Array.from(new Set(numbers)).sort((a, b) => a - b);
    },

    prettyFormat() {
      this.daysText = (this.model.days || []).join(', ');
    },

    findFirstTooBigToken(val) {
      if (val == null) return null;
      const tokens = String(val)
          .replace(/[;|\n]/g, ',')
          .split(/[,\s]+/);

      for (const t of tokens) {
        if (!t) continue;
        if (!/^\d+$/.test(t)) continue;

        const n = parseInt(t, 10);
        if (n < 1 || n > 31) return t;
      }
      return null;
    },

    findDuplicateToken(val) {
      if (val == null) return null;
      const seen = new Set();
      const tokens = String(val)
          .replace(/[;|\n]/g, ',')
          .split(/[,\s]+/);

      for (const t of tokens) {
        if (!t) continue;
        if (!/^\d+$/.test(t)) continue;

        const n = parseInt(t, 10);
        if (n < 1 || n > 31) continue;
        if (seen.has(n)) return n;
        seen.add(n);
      }
      return null;
    },

    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new SmartOfferChristmasConfig();

      if (!Array.isArray(this.model.days)) {
        this.model.days = this.parseDays(this.model.days || '');
      }
      this.daysText = (this.model.days || []).join(', ');

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      if (this.model.valid()) {
        this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject();
      this.discard();
    },
    discard() {
      this.dialog = false;
      this.stopResize();
    },

    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },

    onResize(e) {
      if (!this.resizing) return;
      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;
      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;
      const minWidth = 450;
      const minHeight = 320;
      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;
      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },

    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },

    onenter() {
      this.save();
    }
  },

  mounted() {
    if (!Array.isArray(this.model.days)) {
      this.model.days = this.parseDays(this.model.days || '');
    }
    this.daysText = (this.model.days || []).join(', ');
  },

  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
}
</script>

<style scoped>
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}
.resizable-card__body {
  flex: 1;
  overflow: auto;
}
.resizable-card__actions {
  flex-shrink: 0;
}
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>